import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarBlank, Check, Prohibit, CaretLeft, CaretRight } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AvailabilityCalendar = ({ listingId, onBlockedDatesLoaded }) => {
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const today = new Date();
        const futureDate = new Date(today);
        futureDate.setDate(futureDate.getDate() + 90);
        const fromStr = today.toISOString().split('T')[0];
        const toStr = futureDate.toISOString().split('T')[0];

        const res = await axios.get(`${API_URL}/api/availability/${listingId}?from_date=${fromStr}&to_date=${toStr}`);
        const blocked = new Set(res.data.blocked_dates || []);
        setBlockedDates(blocked);
        if (onBlockedDatesLoaded) onBlockedDatesLoaded(blocked);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAvailability();
  }, [listingId, onBlockedDatesLoaded]);

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  if (loading) {
    return <div className="animate-pulse bg-slate-100 rounded-xl h-40" />;
  }

  if (blockedDates.size === 0) {
    return (
      <div data-testid="availability-calendar">
        <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <CalendarBlank size={20} /> Availability
        </h2>
        <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3">
          <Check size={18} weight="bold" />
          <span className="text-sm font-medium">All dates available for booking</span>
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div data-testid="availability-calendar">
      <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <CalendarBlank size={20} /> Availability
      </h2>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
          className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="buyer-prev-month">
          <CaretLeft size={16} className="text-slate-600" />
        </button>
        <span className="text-sm font-semibold text-slate-700" data-testid="buyer-current-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
          className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="buyer-next-month">
          <CaretRight size={16} className="text-slate-600" />
        </button>
      </div>

      {/* Mini calendar */}
      <div className="grid grid-cols-7 gap-1">
        {dayLabels.map((d, i) => (
          <div key={`h-${i}`} className="text-center text-[10px] font-semibold text-slate-400 py-1">{d}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="h-8" />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          const isPast = dateObj < today;
          const isBlocked = blockedDates.has(dateStr);
          const isToday = dateStr === todayStr;

          return (
            <div key={dateStr}
              className={`h-8 rounded-md text-xs font-medium flex items-center justify-center
                ${isPast ? 'text-slate-300 bg-slate-50' :
                  isBlocked ? 'bg-red-100 text-red-600' :
                  'bg-emerald-50 text-emerald-700'}
                ${isToday ? 'ring-1 ring-blue-400' : ''}
              `}
              data-testid={`buyer-date-${dateStr}`}
            >
              {day}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 text-[11px]">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" />
          <span className="text-slate-500">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-100 border border-red-200" />
          <span className="text-slate-500">Unavailable</span>
        </div>
      </div>

      {blockedDates.size > 0 && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <Prohibit size={12} weight="bold" />
          {blockedDates.size} date(s) unavailable in the next 90 days
        </p>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
